import { createContext, type ReactNode } from 'react';

export interface ExperimentalDesignSystemContextValue {
  isExperimentalForms: boolean;
  /**
   * Whether the experimental design-system *button colour* rework is active for
   * the tones still held behind it — the success family (`success`,
   * `success_ghost`, `codes`) on the reworked `semantic-*` /
   * `button-background-*` tokens. The destructive family moved to the forms flag
   * in DS-1567. This is the raw flag value; it only takes visual effect inside
   * the forms-experimental button, so consumers gate on
   * `isExperimentalForms && isExperimentalButtons`.
   *
   * Optional (defaults to `false` via `useIsExperimentalDesignSystemButtons`) so
   * existing `value={{ isExperimentalForms }}` call sites don't all have to be
   * updated; the `DesignSystemProvider` always supplies it.
   */
  isExperimentalButtons?: boolean;
  /**
   * Whether branded glyphs replace lucide icons app-wide (see
   * `icons/heidi-product-glyphs/`). Read via `useIsBrandedIcons`. Optional for
   * the same call-site-compat reason as `isExperimentalButtons`.
   */
  isBrandedIcons?: boolean;
}

/**
 * React-only context for experimental design-system styles.
 * Host apps mount `ExperimentalDesignSystemContextProvider` with flag-derived
 * values; components read state via `useContext(ExperimentalDesignSystemContext)`
 * or the design-system hooks that subscribe to this context.
 */
export const ExperimentalDesignSystemContext =
  createContext<ExperimentalDesignSystemContextValue>({
    isExperimentalForms: false,
    isExperimentalButtons: false,
    isBrandedIcons: false,
  });

export function ExperimentalDesignSystemContextProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: ExperimentalDesignSystemContextValue;
}) {
  return (
    <ExperimentalDesignSystemContext.Provider value={value}>
      {children}
    </ExperimentalDesignSystemContext.Provider>
  );
}
